﻿import { useEffect, useRef, useState } from "react";
import { Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import introJs from "intro.js";
import Select from "react-select";
import Swal from "sweetalert2";

import { useUploadInventoryExcel } from "@/hooks/implementation/initialLoad";
import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useLoadUnitInventory } from "@/hooks/implementation/unitInventoryLoad";

import { ReactComponent as ExcelLoadIcon } from "@/assets/images/desktop/plantillaexcel.svg";
import { ReactComponent as AddIcon } from "@/assets/images/desktop/agregar.svg";
import { ReactComponent as DownloadIcon } from "@/assets/images/desktop/descargar1.svg";
import { ReactComponent as ManualLoadIcon } from "@/assets/images/desktop/listcheck.svg";

import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import Fetching from "@/components/Fetching/Fetching";
import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { customSelectorStyle } from "@/components/ReactSelect/NoOptions.styles";

import { CHANGE_TAGS_AVAILABLE } from "@/redux/types/inventory.types";
import { validDate } from "@/services/dates.services";

import UnitLoad from "./UnitLoad";
import classes from "./loadinventory.module.scss";
import PrintLabelBtn from "./Buttons/PrintLabelButton";
import FormPrint from "../Print/FormPrint";
import FooterButtons from "./Buttons/FooterButtons";

const steps = [
	{
		element: null,
		intro: "Se ha completado correctamente la implementación Inicial 👏 ",
	},
	{
		element: "#profile-configuration",
		intro: "En la configuración puede visualizar toda la información que ingresó en la implementación inicial",
	},
];

const ProductLoader = (props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { data: response, isLoading } = useInitialDataImplementation();
	const { mutate, isLoading: isMutating } = useUploadInventoryExcel();
	const { mutate: mutateUnitLoad, isLoading: isMutateLoading } = useLoadUnitInventory();

	const { customer } = useSelector((state) => state.auth.user);
	const { filter } = useSelector((state) => state.auth);
	const { tags_available } = useSelector((state) => state.inventory);
	const { type_currency, search } = filter;

	const refFile = useRef(null);
	const introInstance = useRef(null);

	const [data, setData] = useState({
		business_id: "",
		estate_parent_id: "",
		estate_id: "",
		type_currency_id: "",
		items: [],
		is_implementation_completed: null,
	});

	const [showPrint, setShowPrint] = useState(false);
	const [products, setProducts] = useState([]);
	const [productsGenerics, setProductsGenerics] = useState([]);
	const [business, setBusiness] = useState([]);
	const [estates, setEstates] = useState([]);
	const [estatesParents, setEstatesParents] = useState([]);
	const [typesCurrency, setTypesCurrency] = useState([]);
	const [tagsAvailable, setTagsAvailable] = useState(0);
	const [unitaryLoadSelected, setUnitaryLoadSelected] = useState(false);

	const { units_measurement } = response ? response : {};

	useEffect(() => {
		let newData = { ...data };

		if (response && response.products && response.products.length > 0) {
			setProducts(response.products);
		}
		if (response && response.products_generic && response.products_generic.length > 0) {
			setProductsGenerics(response.products_generic);
		}
		if (response && response.business && response.business.length > 0) {
			setBusiness(response.business);

			if (response.business.length === 1) {
				newData.business_id = response.business[0].value;
			}
		}
		if (response && response.estateParents && response.estateParents.length > 0) {
			setEstatesParents(response.estateParents);

			if (response.estateParents.length === 1) {
				newData.estate_parent_id = response.estateParents[0].value;
			}
		}
		if (response && response.estates && response.estates.length > 0) {
			setEstates(response.estates);

			if (response.estates.length === 1) {
				newData.estate_id = response.estates[0].value;
			}
		}
		if (response && response.types_currency && response.types_currency.length > 0) {
			setTypesCurrency(response.types_currency);
		}
		if (response && response.isComplete) {
			newData.is_implementation_completed = response.isComplete;
		}

		setData(newData);

		// eslint-disable-next-line
	}, [response]);

	useEffect(() => {
		let countTags = 0;

		tags_available.forEach((tag) => {
			countTags += Number(tag.quantity_available);
			setTagsAvailable(countTags);
		});
	}, [tags_available]);

	const handleShowPrint = () => {
		setShowPrint(true);
	};

	const uploadFile = (e) => {
		if (!data.business_id) {
			toast.error("Debe ingresar la razón social.");
			return;
		}

		if (!data.estate_id) {
			toast.error("Debe seleccionar el campo.");
			return;
		}

		if (!data.type_currency_id) {
			toast.error("Debe seleccionar la moneda.");
			return;
		}

		const formData = new FormData();
		formData.append("business_id", data.business_id);
		formData.append("estate_parent_id", data.estate_parent_id);
		formData.append("estate_id", data.estate_id);
		formData.append("type_currency_id", data.type_currency_id);
		formData.append("file", e.target.files[0]);

		mutate(formData, {
			onSuccess: (response) => {
				if (response?.isAxiosError || response?.response?.data?.status === "error") {
					if (refFile.current) refFile.current.value = "";
					return;
				}

				if (response) {
					toast.success("Carga de excel completada exitosamente!");
					let newProducts = [...products];
					let newProductsGenerics = [...productsGenerics];
					const rowItems = [];

					response.rows.forEach((row) => {
						const productExist = products.some((prod) => Number(prod.value) === Number(row.product_id));
						const genericExist = productsGenerics.some((prod) => Number(prod.value) === Number(row.save_as));

						if (row.unit_measurement?.toLowerCase() === "gr") {
							row.unit_measurement = "g";
						}
						if (row.unit_measurement?.toLowerCase() === "lt") {
							row.unit_measurement = "l";
						}

						const baseUnit = units_measurement.find((unit) => unit.label?.toLowerCase() === row.unit_measurement?.toLowerCase())?.base_unit;

						const unitValue = units_measurement.find((unit) => unit.label?.toLowerCase() === row.unit_measurement?.toLowerCase())?.value;

						if (!productExist) {
							newProducts.push({
								id: "",
								value: row.product_id || "",
								label: row.product_name || row.product_id || "",
								generic: 0,
								unit_measurement: baseUnit,
							});
						}
						if (row.save_as && !genericExist) {
							newProductsGenerics.push({
								id: "",
								value: row.save_as || "",
								label: row.save_as || "",
								generic: 1,
								estate_id: row.estate_id || "",
								unit_measurement: baseUnit,
							});
						}

						rowItems.push({
							text_product: "",
							tradename: "",
							product_id: row.product_id,
							container_quantity: row.quantity,
							container_content: String(row.container_content).replace(".", ","),
							unit_measurement: unitValue,
							total_price: row.price_total || "",
							expiration_date: row.expiration_date || "",
							generic: row.save_as || "",
							product_suggestions: row.product_suggestions || "",
							generic_suggestions: row.generic_suggestions || "",
							lot_production: row.lot_production || "",
						});
					});

					setProducts(newProducts);
					setProductsGenerics(newProductsGenerics);
					setData({
						...data,
						items: rowItems,
					});
					setUnitaryLoadSelected(true);
				}
			},
		});
	};

	const handleUnitLoadSelected = () => {
		if (!data.business_id) {
			toast.error("Debe ingresar la razón social.");
			return;
		}
		if (!data.estate_id) {
			toast.error("Debe seleccionar el campo.");
			return;
		}
		if (!data.type_currency_id) {
			toast.error("Debe seleccionar la moneda.");
			return;
		}
		setUnitaryLoadSelected(true);
	};

	const exportExcel = async () => {
		const downloadFile = "https://d3inv09b4yglev.cloudfront.net/base/Plantilla Agroinventario.xlsx";
		window.open(downloadFile, "_blank", "noopener,noreferrer");
	};

	const changeBusiness = (value, meta) => {
		setData({
			...data,
			business_id: value,
		});
		if (meta.action === "create-option") {
			setBusiness([...business, { value: value, label: value }]);
		}
	};

	const changeEstatesParent = (value, meta) => {
		setData({
			...data,
			estate_parent_id: value,
			estate_id: "",
		});
		if (meta.action === "create-option") {
			setEstatesParents([...estatesParents, { value: value, label: value }]);
		}
	};

	const changeEstates = (opt, meta) => {
		setData({
			...data,
			estate_id: opt?.value,
		});
		if (meta.action === "create-option") {
			setEstates([...estates, { value: opt?.value, label: opt?.value, estate_parent_id: data.estate_parent_id }]);
		}
	};

	const withItemsActionsAlert = {
		icon: "question",
		showConfirmButton: true,
		showCancelButton: true,
		showDenyButton: true,
		title: "Carga de productos",
		allowOutsideClick: false,
		html: `Cargaras ${data.items.length} producto${data.items.length > 1 ? "s" : ""}, ¿Que deseas hacer posteriormente?`,
		confirmButtonText: "Finalizar carga inicial",
		denyButtonText: "Cargar más productos",
		customClass: {
			confirmButton: classes.customConfirmBtn,
			denyButton: classes.customCancelBtn,
		},
		didOpen: () => {
			document.body.classList.remove("swal2-height-auto");
		},
	};

	const withNoItemsActionsAlert = {
		icon: "info",
		showConfirmButton: true,
		showCancelButton: true,
		confirmButtonText: "Finalizar Implementacion",
		cancelButtonText: "Cancelar",
		title: "Carga inicial",
		html: "<strong>NO</strong> tienes productos cargados para inventario<br/><br/>¿Realmente deseas terminar la carga inicial?",
		allowOutsideClick: false,
		customClass: {
			confirmButton: classes.customConfirmBtn,
			cancelButton: classes.customCancelBtn,
		},
		didOpen: () => {
			document.body.classList.remove("swal2-height-auto");
		},
	};

	const validateItems = () => {
		Swal.fire(withNoItemsActionsAlert).then((result) => {
			if (result.isConfirmed) {
				if (result.isConfirmed) {
					data.is_implementation_completed = true;

					mutateUnitLoad(data, {
						onSuccess: () => {
							startTour();
							return <Fetching />;
						},
					});
				}
			} else {
				toast.info("Accion cancelada por el usuario");
			}
		});
	};

	const validateForm = () => {
		let isValid = true;

		if (!data.business_id) {
			toast.error("Debe ingresar la razón social.");
			isValid = false;
		}
		if (!data.estate_id) {
			toast.error("Debe seleccionar el campo.");
			isValid = false;
		}
		if (!data.type_currency_id) {
			toast.error("Debe seleccionar la moneda.");
			isValid = false;
		}
		if (!data.items.length > 0) {
			toast.error("Debe ingresar al menos un producto.");
			isValid = false;
		}

		data.items.forEach((item, index) => {
			const lineNumber = index + 1;
			if (!item.product_id) {
				toast.error(`Línea ${lineNumber}: Debe seleccionar un producto.`);
				isValid = false;
				return;
			}
			if (!item.container_content) {
				toast.error(`Línea ${lineNumber}: Debe ingresar el contenido por envase.`);
				isValid = false;
				return;
			}
			if (!item.container_quantity) {
				toast.error(`Línea ${lineNumber}: Debe ingresar la cantidad de envases.`);
				isValid = false;
				return;
			}
			if (!item.unit_measurement) {
				toast.error(`Línea ${lineNumber}: Debe seleccionar la unidad de medida.`);
				isValid = false;
				return;
			}
			if (item.expiration_date && !validDate(item.expiration_date)) {
				toast.error(`Línea ${lineNumber}: La fecha de expiración debe ser menor a 01-01-3000.`);
				isValid = false;
				return;
			}
			if (item.expiration_date && new Date(item.expiration_date).toISOString().startsWith("1900-01-01")) {
				toast.error(
					`Línea ${lineNumber}: La fecha 01-01-1900 indica un error en la fecha ingresada. Por favor, verifica la fecha de expiración e intenta de nuevo.`,
				);
				isValid = false;
				return;
			}
		});

		return isValid;
	};

	const handleSave = () => {
		if (data.items.length === 0) {
			validateItems();
			return;
		}

		if (validateForm()) {
			Swal.fire(withItemsActionsAlert).then((result) => {
				if (result.isConfirmed) {
					data.is_implementation_completed = true;

					mutateUnitLoad(data, {
						onSuccess: () => {
							startTour();
							return <Fetching />;
						},
					});
				} else if (result.isDenied) {
					data.is_implementation_completed = false;

					mutateUnitLoad(data, {
						onSuccess: (responseLoad) => {
							setData({
								business_id: "",
								estate_parent_id: "",
								estate_id: "",
								type_currency_id: "",
								items: [],
								is_implementation_completed: false,
							});

							toast.success("Carga completada correctamente");

							dispatch({
								type: CHANGE_TAGS_AVAILABLE,
								payload: {
									tags_available: responseLoad.tags_available,
									lots_available: [],
								},
							});
						},
					});

					setUnitaryLoadSelected(false);
				} else {
					toast.info("Accion cancelada");
				}
			});
		}
	};

	const startTour = () => {
		introInstance.current = introJs();
		introInstance.current.setOptions({
			steps: steps,
			exitOnOverlayClick: false,
			exitOnEsc: false,
			showProgress: true,
			showBullets: true,
			prevLabel: "Anterior",
			nextLabel: "Siguiente",
			doneLabel: "Ir a la configuración",
		});
		introInstance.current.start();

		introInstance.current.onchange((targetElement) => {
			if (targetElement && targetElement.id === "profile-configuration") {
				document.getElementById("profile-dropdown").style.display = "initial";
				setTimeout(() => {
					introInstance.current.refresh();
				}, 700);
			}
		});

		introInstance.current.oncomplete(() => {
			navigate({
				pathname: "/configuracion",
				state: { from: "tour" },
			});
		});
	};

	const DescriptionProductLoad = () => {
		return (
			<div style={{ textAlign: "justify", fontSize: "10px" }}>
				<p>Antes de iniciar la descarga debes indicar la razon social, campo y bodega donde se cargaran los productos</p>
				<p>Luego de eso, cargaremos los productos al inventario, pudiendo realizar esta accion como una carga masiva en Excel o uno a uno.</p>
			</div>
		);
	};

	return (
		<>
			<CustomProcessingModal open={isMutating} message="Procesando carga de Excel..." />
			<CustomProcessingModal open={isMutateLoading} message="Guardando inventario inicial..." />
			{!unitaryLoadSelected ? (
				<div className={classes.main_content}>
					<div className={classes.load_inventory}>
						{tagsAvailable > 0 && !response.isComplete && (
							<>
								<PrintLabelBtn conteo={tagsAvailable} handleShow={handleShowPrint} />
								<FormPrint
									showPrint={showPrint}
									setShowPrint={setShowPrint}
									search={search}
									page={1}
									paginate={1000}
									defaultUnit={1000}
									typePrint="new"
									typeCurrency={type_currency}
								/>
							</>
						)}
						<div className={classes.selects_container}>
							<Col md={4} className={classes.divInput}>
								{/* --------- Business --------- */}
								<div className={classes.inputForm}>
									<div className={classes.inputText}>
										<CustomTooltip text={"Razón Social"} description={<DescriptionProductLoad />} />
									</div>
									<div>
										<Select
											value={business && business.filter((b) => String(b.value) === String(data.business_id))}
											options={business}
											menuPortalTarget={document.body}
											menuPlacement="bottom"
											components={{ NoOptionsMessage }}
											placeholder={<div>Seleccione</div>}
											onChange={(opt, meta) => changeBusiness(opt?.value, meta)}
											styles={customSelectorStyle}
										/>
									</div>
								</div>
							</Col>
							{!!customer.use_estates_parents && (
								<>
									<Col md={4}>
										<br />
										<div className={classes.inputForm}>
											<div className={classes.inputText}>
												{customer.customer_screens.find((cs) => cs.screen_id === 38)
													? customer.customer_screens.find((cs) => cs.screen_id === 38)?.name
													: "Campo"}
											</div>
											<div>
												<Select
													value={estatesParents && estatesParents.filter((b) => String(b.value) === String(data.estate_parent_id))}
													options={estatesParents}
													menuPortalTarget={document.body}
													menuPlacement="bottom"
													components={{ NoOptionsMessage }}
													placeholder={<div>Seleccione</div>}
													onChange={(opt, meta) => changeEstatesParent(opt?.value, meta)}
													styles={customSelectorStyle}
												/>
											</div>
										</div>
									</Col>
								</>
							)}
							<br />
							<Col md={4}>
								{/* --------- Estate --------- */}
								<div className={classes.inputForm}>
									<div className={classes.inputText}>
										{customer.customer_screens.find((cs) => cs.screen_id === 51)
											? customer.customer_screens.find((cs) => cs.screen_id === 51)?.name
											: "Bodega"}
									</div>
									<div>
										<Select
											value={estates && estates.filter((b) => String(b.value) === String(data.estate_id))}
											options={
												data.estate_parent_id ? estates.filter((estate) => estate.estate_parent_id === data.estate_parent_id) : estates
											}
											menuPortalTarget={document.body}
											menuPlacement="bottom"
											components={{ NoOptionsMessage }}
											placeholder={<div>Seleccione</div>}
											onChange={(opt, meta) => changeEstates(opt, meta)}
											styles={customSelectorStyle}
										/>
									</div>
								</div>
							</Col>
							<br />
							<Col md={4}>
								{/* --------- Currency --------- */}
								<div className={classes.inputForm}>
									<div className={classes.inputText}>Moneda</div>
									<div>
										<Select
											value={typesCurrency && typesCurrency.filter((b) => Number(b.value) === Number(data.type_currency_id))}
											placeholder={<div>Seleccione</div>}
											options={typesCurrency}
											onChange={(value) =>
												setData({
													...data,
													type_currency_id: value.value,
												})
											}
											components={{ NoOptionsMessage }}
											styles={customSelectorStyle}
										/>
									</div>
								</div>
							</Col>
						</div>
						<div md={12} className={classes.cards_section}>
							<div className={classes.card_container}>
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="tooltip-add">
											{!data.business_id || !data.estate_id || !data.type_currency_id
												? "Para activar esta opcion necesitas seleccionar una razon social, un campo y una moneda"
												: "Desde aqui puedes cargar el template de excel para hacer un registro masivo"}
										</Tooltip>
									}
								>
									<div
										onClick={(_e) =>
											data.business_id && data.estate_id && data.type_currency_id && refFile.current && refFile.current.click()
										}
										className={`${classes.card_div} ${
											(!data.business_id || !data.estate_id || !data.type_currency_id) && classes.disabled
										}`}
									>
										<ExcelLoadIcon className={classes.inventory_icon} />
										<div className={classes.label_row}>
											<input style={{ display: "none" }} type="file" multiple={false} ref={refFile} onChange={uploadFile} />
											<AddIcon />
											<>Cargar Planilla</>
										</div>
									</div>
								</OverlayTrigger>

								<button className={classes.dowload_template_button} onClick={() => exportExcel()}>
									<DownloadIcon />
									<span>Descargar planilla tipo</span>
								</button>
							</div>
							<div className={classes.card_container}>
								<div className={classes.card_div} onClick={() => handleUnitLoadSelected()}>
									<ManualLoadIcon className={classes.inventory_icon} />
									<div className={classes.label_row}>
										<AddIcon />
										<>Carga Unitaria</>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className={classes.main_content}>
					<UnitLoad
						data={data}
						setData={setData}
						setUnitaryLoadSelected={setUnitaryLoadSelected}
						isFetching={isLoading}
						products={products}
						setProducts={setProducts}
						productsGenerics={productsGenerics}
						setProductsGenerics={setProductsGenerics}
						units_measurement={units_measurement}
						lockNextBtn={isMutating || isMutateLoading}
					/>
				</div>
			)}

			{!data.is_implementation_completed && (
				<FooterButtons
					wizard={props.formWizardRef}
					currentIndex={props.index}
					handleSave={handleSave}
					handleFinish={handleSave}
					isLoading={isMutating || isMutateLoading}
				/>
			)}
		</>
	);
};

export default ProductLoader;
